import { Fragment } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import styled from 'styled-components'
import config from '../../config'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'

const MAX_SERIES_COUNT = 30
const MAX_LOTTERY_LIMIT = 50000

const schema = z
  .object({
    number: z
      .string()
      .max(6)
      .default('......')
      .refine((value) => value.match(/\d/g), {
        message: 'กรุณากรอกหมายเลขที่ต้องการนำเข้าสู่ระบบ',
      }),
    amount: z.string().transform((value, ctx) => {
      const number = Number(value.toString().replaceAll(',', ''))
      console.log('🚀 ~ amount:z.string ~ number:', number)

      if (isNaN(number) || number < 1) {
        ctx.addIssue({
          message: 'กรุณากรอกจำนวนที่ต้องการนำเข้าระบบ',
        })

        return z.NEVER
      }

      return number
    }),
    count: z.coerce.number().min(1, 'จำนวนชุดต้องไม่น้อยกว่า 1').default(1),
    includeCharity: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.amount * data.count > MAX_LOTTERY_LIMIT) {
      const message =
        data.count > 1
          ? `จำนวนชุดต้องไม่เกิน ${Math.floor(MAX_LOTTERY_LIMIT / data.count).toLocaleString()} ชุด`
          : 'จำนวนต้องไม่เกิน 50,000 ใบ'
      ctx.addIssue({
        path: ['amount'],
        message: message,
      })

      return z.NEVER
    }
  })

const ImportSeriesByNumberModal = ({ show, onHide }) => {
  const dispatch = useDispatch()

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      number: '......',
      amount: '',
      count: 1,
      includeCharity: false,
    },
    resolver: zodResolver(schema),
  })

  const handleCloseLotteryNumberModal = () => {
    reset()
    onHide?.()
  }

  const handleSubmitForm = async (data) => {
    try {
      dispatch({ type: 'loading_show' })
      const response = await axios.post(
        `${config.apiAdminGateway}/api/v2/admin/lotteries/publish`,
        {
          count: parseInt(data.count),
          amount: parseInt(data.amount) * parseInt(data.count),
          number: data.number,
          ...(data.includeCharity && { includeCharity: true }),
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get('accessToken')}`,
          },
        },
      )

      if (response.data.status.code !== 0) throw new Error(response.data.status.description)

      handleCloseLotteryNumberModal()

      const successAmount = response.data.data.count
      const successAmountCount = successAmount / parseInt(data.count)
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        html:
          `นำหวยเข้าสู่ระบบสำเร็จ<br> จำนวน ${Number(successAmount).toLocaleString()} ใบ` +
          (data.count > 1 ? ` <br> คิดเป็น ${Number(successAmountCount).toLocaleString()} ชุด` : ''),
      })
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: err.message,
      })
    } finally {
      dispatch({ type: 'loading_hide' })
    }
  }

  return (
    <Modal show={show} onHide={handleCloseLotteryNumberModal} centered>
      <Modal.Header style={{ alignItems: 'center' }}>
        <span>เอาหวยเลขท้ายเข้าระบบ</span>
        <Controller
          name="includeCharity"
          control={control}
          render={({ field }) => (
            <CharitySwitchWrapper
              onClick={() => field.onChange(!field.value)}
              role="switch"
              aria-checked={field.value}
            >
              <CharitySwitchTrack $checked={field.value}>
                <CharitySwitchThumb $checked={field.value} />
              </CharitySwitchTrack>
              <CharitySwitchLabel>ลอตเตอรี่การกุศล</CharitySwitchLabel>
            </CharitySwitchWrapper>
          )}
        />
        <Button
          type="button"
          variant=""
          className="close"
          style={{ paddingLeft: '0.5rem' }}
          onClick={handleCloseLotteryNumberModal}
        >
          <span>&times;</span>
        </Button>
      </Modal.Header>
      <Modal.Body style={{ padding: '1rem' }}>
        <Form>
          <Controller
            name="count"
            control={control}
            render={(props) => (
              <Fragment>
                <Form.Label style={{ paddingTop: '8px' }}>จำนวนชุด</Form.Label>
                <Form.Control
                  id="lotteryNumberCount"
                  placeholder="ใส่จำนวนชุด"
                  {...props.field}
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9]+$/.test(e.target.value)) {
                      const value = parseInt(e.target.value) || ''
                      props.field.onChange(value > MAX_SERIES_COUNT ? MAX_SERIES_COUNT : value)
                    }
                  }}
                />
                {errors.count && (
                  <div
                    style={{
                      color: 'red',
                      paddingTop: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {errors.count?.message}
                  </div>
                )}
              </Fragment>
            )}
          />
          <Controller
            name="amount"
            control={control}
            render={(props) => (
              <Fragment>
                <Form.Label style={{ paddingTop: '8px' }}>
                  จำนวนลอตเตอรี่ ({getValues('count') > 1 ? 'ชุด' : 'ใบ'}){' '}
                </Form.Label>
                <Form.Control
                  id="lotteryNumberQuantity"
                  placeholder="ใส่จำนวนลอตเตอรี่"
                  maxLength={10}
                  {...props.field}
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9]+$/.test(e.target.value)) {
                      props.field.onChange(e.target.value)
                    }
                  }}
                  onFocus={(e) => {
                    props.field.onChange(e.target.value.replaceAll(',', ''))
                    props.field.onFocus?.()
                  }}
                  onBlur={(e) => {
                    props.field.onChange((parseInt(e.target.value) || '').toLocaleString())
                    props.field.onBlur?.()
                  }}
                />
                {errors.amount && (
                  <div
                    style={{
                      color: 'red',
                      paddingTop: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {errors.amount?.message}
                  </div>
                )}
              </Fragment>
            )}
          />
          <Form.Label style={{ paddingTop: '8px' }}>ใส่หมายเลข</Form.Label>
          <div style={{ display: 'flex', gap: '4px' }}>
            {watch('number')
              .split('')
              .map((number, index) => (
                <Form.Control
                  key={`lotteryNumber${index}`}
                  id={`lotteryNumber${index}`}
                  maxLength={2}
                  value={number.replace('.', '')}
                  style={{
                    aspectRatio: '1/1',
                    height: '100%',
                    textAlign: 'center',
                  }}
                  onKeyDown={(e) => {
                    if (e.target.value !== '') return
                    if (e.key === 'Backspace') {
                      const previousInput = document.querySelector(`#lotteryNumber${index - 1}`)
                      previousInput && previousInput.focus()
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value

                    if (value !== '' && !/^[0-9]*$/g.test(value)) return

                    if (value.length > 1) {
                      value = value.match(new RegExp(`[^${number}]`, 'g'))?.[0] || number
                    }

                    const numbers = getValues('number').split('')
                    numbers[index] = value || '.'
                    setValue('number', numbers.join(''), { shouldTouch: true, shouldDirty: true, shouldValidate: true })

                    if (e.nativeEvent.inputType === 'insertText') {
                      if (getValues().number.length + 1 === index) return

                      const nextInput = document.querySelector(`#lotteryNumber${index + 1}`)
                      nextInput && nextInput.focus()
                    }
                  }}
                />
              ))}
          </div>
          {errors.number && (
            <div
              style={{
                color: 'red',
                paddingTop: '4px',
                fontSize: '12px',
              }}
            >
              {errors.number?.message}
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="danger light" onClick={handleCloseLotteryNumberModal}>
          ยกเลิก
        </Button>
        <Button
          variant="primary light"
          onClick={(e) => {
            e.preventDefault()
            handleSubmit(handleSubmitForm)()
          }}
        >
          ยืนยัน
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ImportSeriesByNumberModal

const CharitySwitchWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 2.25rem;
  cursor: pointer;
  user-select: none;
`

const CharitySwitchTrack = styled.div`
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background-color: ${(props) => (props.$checked ? '#0b2a97' : '#e2e2e2')};
  border: 1px solid ${(props) => (props.$checked ? '#0b2a97' : '#d5d5d5')};
  transition: background-color 0.2s ease, border-color 0.2s ease;
  flex-shrink: 0;
`

const CharitySwitchThumb = styled.div`
  position: absolute;
  top: 1px;
  left: 1px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transform: ${(props) => (props.$checked ? 'translateX(18px)' : 'translateX(0)')};
  transition: transform 0.2s ease;
`

const CharitySwitchLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #3d3d3d;
  line-height: 1;
`
